/**
 * Per-match action history log (T143).
 *
 * Tracks every scoring action the operator has taken — saves, undoes,
 * batch undoes, redoes, edits — so the history scrubber panel can
 * render a Photoshop-style timeline. Click any row to inspect or
 * navigate back.
 *
 * Persisted per match in localStorage so a refresh doesn't blow it
 * away. Capped at 200 entries — beyond that the operator probably
 * wants the QC tools, not live history.
 *
 * The log is INFORMATIONAL — clicking a row doesn't reverse an action
 * (that's what undo/redo are for). It opens the relevant ball in
 * Edit Ball if the ball still exists. Undo entries are read-only.
 */
import { useCallback, useEffect, useState } from "react";


const MAX_ENTRIES = 200;
const STORAGE_PREFIX = "zcd_history_log_";


function loadLog(matchId) {
  if (!matchId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + matchId);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(-MAX_ENTRIES) : [];
  } catch { return []; }
}


function saveLog(matchId, log) {
  if (!matchId) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + matchId, JSON.stringify(log.slice(-MAX_ENTRIES)));
  } catch {
    // Quota — drop the oldest half and retry once. If still failing
    // the log just stops persisting; in-memory state still works.
    try {
      localStorage.setItem(
        STORAGE_PREFIX + matchId,
        JSON.stringify(log.slice(-Math.floor(MAX_ENTRIES / 2))),
      );
    } catch { /* give up */ }
  }
}


/**
 * @returns {{ log, push, clear }}
 *   log:   ordered (oldest first) array of entries
 *   push:  (kind, meta) => append a new entry
 *   clear: () => wipe (admin tool / new innings reset)
 */
export function useScoringHistoryLog(matchId) {
  const [log, setLog] = useState(() => loadLog(matchId));

  // Re-load when the match id changes (e.g. operator navigates to a
  // different match without unmounting the parent).
  useEffect(() => { setLog(loadLog(matchId)); }, [matchId]);

  // Mirror to localStorage on every change. The effect approach
  // dedupes writes across multiple push() calls in the same render.
  useEffect(() => { saveLog(matchId, log); }, [matchId, log]);

  const push = useCallback((kind, meta = {}) => {
    setLog((prev) => {
      const entry = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        ts: Date.now(),
        kind,
        ...meta,
      };
      return [...prev, entry].slice(-MAX_ENTRIES);
    });
  }, []);

  const clear = useCallback(() => setLog([]), []);

  return { log, push, clear };
}
